import React, { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, MessageSquare, Newspaper, ClipboardList,
  Building2, GitBranch, Settings, LogOut, Inbox, Trash2, Plus, Save, X, Eye, Image as ImageIcon, KeyRound, CheckCircle2
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PrimaryButton, OutlineButton, inputCls, Field, Toast } from "@/components/ui-kit";
import BrandMedia from "@/pages/admin/BrandMedia";

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
function Sidebar({ tab, setTab, onLogout }) {
  const items = [
    { k: "overview", t: "Overview", i: LayoutDashboard },
    { k: "applications", t: "Driver Applications", i: ClipboardList },
    { k: "leads", t: "Contact Leads", i: Inbox },
    { k: "investor", t: "Investor Inquiries", i: Building2 },
    { k: "testimonials", t: "Testimonials", i: MessageSquare },
    { k: "media", t: "Brand Media", i: ImageIcon },
    { k: "blog", t: "Blog", i: Newspaper },
    { k: "roadmap", t: "Roadmap", i: GitBranch },
    { k: "settings", t: "Fleet Settings", i: Settings },
    { k: "account", t: "Account", i: KeyRound },
  ];
  return (
    <aside className="w-64 shrink-0 border-r border-white/10 bg-[#0A0A0A] p-4 hidden lg:flex flex-col">
      <Link to="/" className="px-3 py-3 font-display text-xl">GK Fleet · <span className="text-[#3B82F6]">Admin</span></Link>
      <nav className="mt-4 space-y-1 flex-1">
        {items.map((it) => (
          <button
            key={it.k}
            data-testid={`admin-tab-${it.k}`}
            onClick={() => setTab(it.k)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition ${
              tab === it.k ? "bg-white/10 text-white" : "text-[#9CA3AF] hover:bg-white/5 hover:text-white"
            }`}
          >
            <it.i className="w-4 h-4" /> {it.t}
          </button>
        ))}
      </nav>
      <button onClick={onLogout} data-testid="admin-logout" className="mt-4 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-[#9CA3AF] hover:bg-white/5 hover:text-white">
        <LogOut className="w-4 h-4" /> Sign out
      </button>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------
function Overview() {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setO(r.data)); }, []);
  const tiles = [
    { l: "New applications", v: o?.applications_new, s: `of ${o?.applications_total} total` },
    { l: "New contact leads", v: o?.leads_new, s: `of ${o?.leads_total} total` },
    { l: "New investor inquiries", v: o?.investor_new, s: `of ${o?.investor_total} total` },
    { l: "Published blog posts", v: o?.blog_published },
    { l: "Published testimonials", v: o?.testimonials_published },
  ];
  return (
    <div>
      <h2 className="font-display text-3xl">Overview</h2>
      <p className="mt-1 text-[#9CA3AF]">A snapshot of what's happening across GK Fleet.</p>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiles.map((t) => (
          <div key={t.l} className="rounded-xl bg-[#1A1A1A] border border-white/10 p-5">
            <p className="text-xs tracking-[0.18em] uppercase text-[#9CA3AF]">{t.l}</p>
            <p className="mt-3 font-display text-4xl font-semibold">{t.v ?? "—"}</p>
            {t.s && <p className="mt-1 text-xs text-[#9CA3AF]">{t.s}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new: "bg-[#3B82F6]/15 text-[#60A5FA] border-[#3B82F6]/30",
    reviewing: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
    approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    rejected: "bg-red-500/15 text-red-300 border-red-500/30",
    contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    closed: "bg-white/10 text-[#9CA3AF] border-white/20",
  };
  return <span className={`px-2 py-1 rounded-md text-[10px] font-medium border tracking-wider uppercase ${map[status] || map.new}`}>{status || "new"}</span>;
}

function LeadTable({ kind, fields, columns }) {
  const [items, setItems] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => { api.get(`/admin/${kind}`).then((r) => setItems(r.data)); }, [kind, tick]);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/${kind}/${id}/status`, { status });
    setTick((t) => t + 1);
  };
  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/admin/${kind}/${id}`);
    setTick((t) => t + 1);
  };

  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-[#9CA3AF]">
            <tr>
              {columns.map((c) => <th key={c} className="text-left px-4 py-3 font-medium">{c}</th>)}
              <th className="text-right px-4 py-3 font-medium">Status / Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {items.map((it) => (
              <tr key={it.id} className="hover:bg-white/[0.02]">
                {fields.map((f) => (
                  <td key={f} className="px-4 py-3 text-[#D1D5DB] align-top">
                    {f === "created_at" ? new Date(it[f]).toLocaleString() : (it[f] || "—")}
                  </td>
                ))}
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <StatusPill status={it.status} />
                    <select value={it.status || "new"} onChange={(e) => updateStatus(it.id, e.target.value)} className="bg-[#1A1A1A] border border-white/10 rounded px-2 py-1 text-xs">
                      <option value="new">new</option>
                      <option value="reviewing">reviewing</option>
                      <option value="contacted">contacted</option>
                      <option value="approved">approved</option>
                      <option value="rejected">rejected</option>
                      <option value="closed">closed</option>
                    </select>
                    <button onClick={() => del(it.id)} className="p-1.5 rounded hover:bg-red-500/10 text-red-400" data-testid={`delete-${kind}-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={fields.length + 1} className="px-4 py-12 text-center text-[#9CA3AF]">No entries yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Testimonials editor
function TestimonialsEditor() {
  const empty = { name: "", role: "Driver Partner", city: "Hyderabad", quote: "", rating: 5, avatar_url: "", is_published: true };
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => { api.get("/admin/testimonials").then((r) => setItems(r.data)); }, [tick]);

  const save = async () => {
    const payload = { ...edit };
    if (edit.id) await api.put(`/admin/testimonials/${edit.id}`, payload);
    else await api.post("/admin/testimonials", payload);
    setEdit(null); setTick((t) => t + 1);
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/testimonials/${id}`); setTick((t) => t + 1);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl">Testimonials</h2>
          <p className="mt-1 text-[#9CA3AF]">What partners say. Shown on the homepage.</p>
        </div>
        <PrimaryButton onClick={() => setEdit({ ...empty })} data-testid="testimonial-new"><Plus className="w-4 h-4" /> Add testimonial</PrimaryButton>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="rounded-xl border border-white/10 bg-[#1A1A1A] p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium">{t.name}</p>
                <p className="text-xs text-[#9CA3AF]">{t.role} · {t.city}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEdit(t)} className="text-xs text-[#3B82F6]" data-testid={`testimonial-edit-${t.id}`}>Edit</button>
                <button onClick={() => del(t.id)} className="text-xs text-red-400" data-testid={`testimonial-del-${t.id}`}>Delete</button>
              </div>
            </div>
            <p className="mt-3 text-[#D1D5DB] text-sm leading-relaxed">"{t.quote}"</p>
            <p className="mt-3 text-[10px] tracking-wider uppercase text-[#9CA3AF]">{t.is_published ? "Published" : "Draft"}</p>
          </div>
        ))}
      </div>

      {edit && (
        <Modal onClose={() => setEdit(null)} title={edit.id ? "Edit testimonial" : "New testimonial"}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name"><input className={inputCls} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Role"><input className={inputCls} value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} /></Field>
            <Field label="City"><input className={inputCls} value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} /></Field>
            <Field label="Rating (1-5)"><input type="number" min={1} max={5} className={inputCls} value={edit.rating} onChange={(e) => setEdit({ ...edit, rating: parseInt(e.target.value || 5) })} /></Field>
            <div className="md:col-span-2"><Field label="Quote"><textarea rows={4} className={inputCls} value={edit.quote} onChange={(e) => setEdit({ ...edit, quote: e.target.value })} /></Field></div>
            <div className="md:col-span-2 flex items-center gap-2">
              <input id="pub" type="checkbox" checked={edit.is_published} onChange={(e) => setEdit({ ...edit, is_published: e.target.checked })} />
              <label htmlFor="pub" className="text-sm text-[#D1D5DB]">Published</label>
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <PrimaryButton onClick={save} data-testid="testimonial-save"><Save className="w-4 h-4" /> Save</PrimaryButton>
            <OutlineButton onClick={() => setEdit(null)}>Cancel</OutlineButton>
          </div>
        </Modal>
      )}
    </div>
  );
}

function BlogEditor() {
  const empty = { title: "", slug: "", category: "Company Updates", excerpt: "", content: "", cover_image: "", author: "GK Fleet Team", is_published: true };
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/admin/blog").then((r) => setItems(r.data)); }, [tick]);

  const slugify = (s) => s.toLowerCase().replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-");

  const save = async () => {
    const payload = { ...edit, slug: edit.slug || slugify(edit.title) };
    if (edit.id) await api.put(`/admin/blog/${edit.id}`, payload);
    else await api.post("/admin/blog", payload);
    setEdit(null); setTick((t) => t + 1);
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/blog/${id}`); setTick((t) => t + 1); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h2 className="font-display text-3xl">Blog</h2><p className="mt-1 text-[#9CA3AF]">Posts published on /blog.</p></div>
        <PrimaryButton onClick={() => setEdit({ ...empty })} data-testid="blog-new"><Plus className="w-4 h-4" /> New post</PrimaryButton>
      </div>
      <div className="mt-8 space-y-3">
        {items.map((p) => (
          <div key={p.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#1A1A1A] p-4">
            <div>
              <p className="font-display text-lg">{p.title}</p>
              <p className="text-xs text-[#9CA3AF] mt-1">{p.category} · /{p.slug} · {p.is_published ? "Published" : "Draft"}</p>
            </div>
            <div className="flex gap-3">
              <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[#9CA3AF] hover:text-white inline-flex items-center gap-1"><Eye className="w-3 h-3" /> View</a>
              <button onClick={() => setEdit(p)} className="text-xs text-[#3B82F6]" data-testid={`blog-edit-${p.id}`}>Edit</button>
              <button onClick={() => del(p.id)} className="text-xs text-red-400" data-testid={`blog-del-${p.id}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <Modal onClose={() => setEdit(null)} title={edit.id ? "Edit post" : "New post"} wide>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title"><input className={inputCls} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value, slug: edit.slug || slugify(e.target.value) })} /></Field>
            <Field label="Slug"><input className={inputCls} value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: slugify(e.target.value) })} /></Field>
            <Field label="Category">
              <select className={inputCls} value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })}>
                <option>Driver Tips</option><option>Fleet Management</option><option>Industry News</option><option>Company Updates</option><option>Business Growth</option>
              </select>
            </Field>
            <Field label="Cover image URL"><input className={inputCls} value={edit.cover_image} onChange={(e) => setEdit({ ...edit, cover_image: e.target.value })} placeholder="https://..." /></Field>
            <div className="md:col-span-2"><Field label="Excerpt"><textarea rows={2} className={inputCls} value={edit.excerpt} onChange={(e) => setEdit({ ...edit, excerpt: e.target.value })} /></Field></div>
            <div className="md:col-span-2"><Field label="Content (one paragraph per line)"><textarea rows={10} className={inputCls} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} /></Field></div>
            <div className="md:col-span-2 flex items-center gap-2">
              <input id="bpub" type="checkbox" checked={edit.is_published} onChange={(e) => setEdit({ ...edit, is_published: e.target.checked })} />
              <label htmlFor="bpub" className="text-sm text-[#D1D5DB]">Published</label>
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <PrimaryButton onClick={save} data-testid="blog-save"><Save className="w-4 h-4" /> Save</PrimaryButton>
            <OutlineButton onClick={() => setEdit(null)}>Cancel</OutlineButton>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RoadmapEditor() {
  const empty = { year: 2026, vehicles: 14, title: "", description: "", order: 0 };
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/admin/roadmap").then((r) => setItems(r.data)); }, [tick]);
  const save = async () => {
    const payload = { ...edit, year: parseInt(edit.year), vehicles: parseInt(edit.vehicles), order: parseInt(edit.order || 0) };
    if (edit.id) await api.put(`/admin/roadmap/${edit.id}`, payload);
    else await api.post("/admin/roadmap", payload);
    setEdit(null); setTick((t) => t + 1);
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/roadmap/${id}`); setTick((t) => t + 1); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h2 className="font-display text-3xl">Growth roadmap</h2><p className="mt-1 text-[#9CA3AF]">Milestones shown on the homepage.</p></div>
        <PrimaryButton onClick={() => setEdit({ ...empty })} data-testid="roadmap-new"><Plus className="w-4 h-4" /> Add milestone</PrimaryButton>
      </div>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((m) => (
          <div key={m.id} className="rounded-xl border border-white/10 bg-[#1A1A1A] p-5">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[#3B82F6]">{m.year}</p>
              <div className="flex gap-3">
                <button onClick={() => setEdit(m)} className="text-xs text-[#3B82F6]">Edit</button>
                <button onClick={() => del(m.id)} className="text-xs text-red-400">Delete</button>
              </div>
            </div>
            <p className="mt-3 font-display text-3xl">{m.vehicles} vehicles</p>
            <p className="mt-1 text-sm text-[#D1D5DB]">{m.title}</p>
            {m.description && <p className="mt-2 text-xs text-[#9CA3AF]">{m.description}</p>}
          </div>
        ))}
      </div>
      {edit && (
        <Modal onClose={() => setEdit(null)} title={edit.id ? "Edit milestone" : "New milestone"}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Year"><input type="number" className={inputCls} value={edit.year} onChange={(e) => setEdit({ ...edit, year: e.target.value })} /></Field>
            <Field label="Vehicles"><input type="number" className={inputCls} value={edit.vehicles} onChange={(e) => setEdit({ ...edit, vehicles: e.target.value })} /></Field>
            <Field label="Title"><input className={inputCls} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></Field>
            <Field label="Order"><input type="number" className={inputCls} value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} /></Field>
            <div className="col-span-2"><Field label="Description"><textarea rows={3} className={inputCls} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field></div>
          </div>
          <div className="mt-6 flex gap-3">
            <PrimaryButton onClick={save}><Save className="w-4 h-4" /> Save</PrimaryButton>
            <OutlineButton onClick={() => setEdit(null)}>Cancel</OutlineButton>
          </div>
        </Modal>
      )}
    </div>
  );
}

function FleetSettings({ onSaved }) {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState("");
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => {
    await api.put("/admin/stats", {
      vehicles_current: parseInt(s.vehicles_current),
      uptime_percent: parseInt(s.uptime_percent),
      primary_vehicle: s.primary_vehicle,
      city: s.city,
      target_year: parseInt(s.target_year),
      target_vehicles: parseInt(s.target_vehicles),
    });
    setSaved("Saved");
    setTimeout(() => setSaved(""), 2000);
    onSaved?.();
  };
  return (
    <div>
      <h2 className="font-display text-3xl">Fleet settings</h2>
      <p className="mt-1 text-[#9CA3AF]">Numbers shown across the homepage and dashboard.</p>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        <Field label="Current vehicles"><input type="number" className={inputCls} value={s.vehicles_current} onChange={(e) => setS({ ...s, vehicles_current: e.target.value })} /></Field>
        <Field label="Uptime %"><input type="number" className={inputCls} value={s.uptime_percent} onChange={(e) => setS({ ...s, uptime_percent: e.target.value })} /></Field>
        <Field label="Primary vehicle"><input className={inputCls} value={s.primary_vehicle} onChange={(e) => setS({ ...s, primary_vehicle: e.target.value })} /></Field>
        <Field label="City"><input className={inputCls} value={s.city} onChange={(e) => setS({ ...s, city: e.target.value })} /></Field>
        <Field label="Target year"><input type="number" className={inputCls} value={s.target_year} onChange={(e) => setS({ ...s, target_year: e.target.value })} /></Field>
        <Field label="Target vehicles"><input type="number" className={inputCls} value={s.target_vehicles} onChange={(e) => setS({ ...s, target_vehicles: e.target.value })} /></Field>
      </div>
      <div className="mt-6 flex gap-3 items-center">
        <PrimaryButton onClick={save} data-testid="fleet-save"><Save className="w-4 h-4" /> Save</PrimaryButton>
        {saved && <span className="text-sm text-emerald-400">{saved}</span>}
      </div>
    </div>
  );
}

function AccountSettings() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk(false);
    if (form.new_password.length < 8) return setErr("New password must be at least 8 characters.");
    if (form.new_password !== form.confirm) return setErr("New password and confirmation don't match.");
    if (form.current_password === form.new_password) return setErr("New password must be different from current.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setOk(true);
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not change password. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="account-settings">
      <h2 className="font-display text-3xl">Account</h2>
      <p className="mt-1 text-[#9CA3AF]">Manage your admin credentials.</p>

      <div className="mt-8 max-w-xl space-y-6">
        <div className="rounded-xl border border-white/10 bg-[#1A1A1A] p-5">
          <p className="text-xs tracking-[0.18em] uppercase text-[#9CA3AF]">Signed in as</p>
          <p className="mt-2 font-display text-xl">{user?.email || "—"}</p>
          <p className="mt-1 text-xs text-[#9CA3AF]">Role: {user?.role || "admin"}</p>
        </div>

        <form onSubmit={submit} className="rounded-xl border border-white/10 bg-[#1A1A1A] p-6 space-y-4">
          <div className="flex items-center gap-2 text-[#3B82F6]">
            <KeyRound className="w-4 h-4" />
            <p className="text-xs tracking-[0.18em] uppercase font-semibold">Change password</p>
          </div>

          <Field label="Current password">
            <input
              data-testid="pw-current"
              type="password"
              required
              autoComplete="current-password"
              className={inputCls}
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            />
          </Field>
          <Field label="New password" hint="At least 8 characters. Use a mix of letters, numbers, and symbols.">
            <input
              data-testid="pw-new"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              className={inputCls}
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            />
          </Field>
          <Field label="Confirm new password">
            <input
              data-testid="pw-confirm"
              type="password"
              required
              autoComplete="new-password"
              className={inputCls}
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            />
          </Field>

          {err && <p data-testid="pw-error" className="text-sm text-red-400">{err}</p>}
          {ok && (
            <p data-testid="pw-success" className="text-sm text-emerald-400 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Password updated successfully.
            </p>
          )}

          <div className="pt-2">
            <PrimaryButton data-testid="pw-submit" disabled={busy} type="submit">
              <Save className="w-4 h-4" /> {busy ? "Updating…" : "Update password"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function Modal({ children, onClose, title, wide }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`w-full ${wide ? "max-w-3xl" : "max-w-xl"} max-h-[90vh] overflow-y-auto rounded-2xl bg-[#0A0A0A] border border-white/10 p-6 md:p-8`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5 text-[#9CA3AF]"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function AdminPanel() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const onLogout = () => { logout(); nav("/admin/login"); };

  const render = () => {
    switch (tab) {
      case "overview": return <Overview />;
      case "applications": return (
        <div><h2 className="font-display text-3xl mb-1">Driver applications</h2><p className="text-[#9CA3AF] mb-6">Drivers who applied to the program.</p>
          <LeadTable kind="driver-applications" fields={["name", "phone", "city", "preferred_platform", "created_at"]} columns={["Name", "Phone", "City", "Platform", "Date"]} />
        </div>
      );
      case "leads": return (
        <div><h2 className="font-display text-3xl mb-1">Contact leads</h2><p className="text-[#9CA3AF] mb-6">Messages submitted via the contact form.</p>
          <LeadTable kind="contact-leads" fields={["name", "email", "phone", "subject", "created_at"]} columns={["Name", "Email", "Phone", "Subject", "Date"]} />
        </div>
      );
      case "investor": return (
        <div><h2 className="font-display text-3xl mb-1">Investor inquiries</h2><p className="text-[#9CA3AF] mb-6">Investors and strategic partners reaching out.</p>
          <LeadTable kind="investor-inquiries" fields={["name", "email", "organization", "investment_range", "created_at"]} columns={["Name", "Email", "Org", "Range", "Date"]} />
        </div>
      );
      case "testimonials": return <TestimonialsEditor />;
      case "media": return <BrandMedia />;
      case "blog": return <BlogEditor />;
      case "roadmap": return <RoadmapEditor />;
      case "settings": return <FleetSettings />;
      case "account": return <AccountSettings />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white" data-testid="admin-panel">
      <Sidebar tab={tab} setTab={setTab} onLogout={onLogout} />
      <div className="flex-1">
        <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between lg:hidden">
          <span className="font-display">GK Fleet Admin</span>
          <select value={tab} onChange={(e) => setTab(e.target.value)} className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-2 text-sm">
            {[
              ["overview", "Overview"], ["applications", "Applications"], ["leads", "Leads"], ["investor", "Investors"],
              ["testimonials", "Testimonials"], ["media", "Brand Media"], ["blog", "Blog"], ["roadmap", "Roadmap"], ["settings", "Settings"], ["account", "Account"],
            ].map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </header>
        <div className="p-6 md:p-10 max-w-6xl">
          {render()}
        </div>
      </div>
    </div>
  );
}
